import { useEffect, useMemo } from 'react';
import { ArrowBack, ArrowForward, Edit, FileCopy } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useParams } from 'react-router-dom';
import { InvoiceFragmentFragment, InvoiceItem, InvoiceStatus, useInvoiceQuery } from '../../../../api/gql';
import { ExpandedBox } from '../../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { SetInvoicePaidBox } from '../../../../components/invoice/InvoiceControlBox';
import { PageHeader } from '../../../../components/PageHeader';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';
import { PageContent, PageLayout } from '../../../../layout';
import type { InvoiceManagementInvoiceFormType } from '../../../../modules/forms/invoice/types';
import { InvoiceArchiveButton } from '../../../../modules/invoice/InvoiceArchiveButton';
import { InvoiceManagementInvoiceForm } from '../../../../modules/invoice-management/InvoiceManagementInvoiceForm';
import { InvoiceManagementInvoiceDownloadButton } from '../../../../modules/invoice-management/pdf/InvoiceManagementInvoiceDownloadButton';
import { InvoiceManagementInvoicePdfData } from '../../../../modules/invoice-management/pdf/InvoiceManagementInvoicePdfDocument';
import { InvoiceManagementSendEmailButton } from '../../../../modules/invoice-management/pdf/InvoiceManagementSendEmailButton';
import { currencies, getPriceWithCurrencyString } from '../../../../utils/currencies';
import { formatDate } from '../../../../utils/format';
import { getInvoiceStateProperties } from '../../../../utils/invoice';
import { notify } from '../../../../utils/notification';
import { replaceNullOrUndefined } from '../../../../utils/object';
import { fromCentesimal } from '../../../../utils/transform';

const TRANS_NS = 'page-invoice-detail';

export const InvoiceManagementInvoiceDetail = () => {
  const { invoiceId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();

  const { data, isLoading, isError } = useInvoiceQuery({
    id: invoiceId ?? '',
  });

  const pdfData = useMemo<InvoiceManagementInvoicePdfData | null>(() => {
    const invoice = data?.invoice;
    if (!invoice) return null;

    return {
      ...invoice,
      amount: getPriceWithCurrencyString(fromCentesimal(invoice.amount ?? 0), invoice.currency),
      currency: currencies.find((curr) => curr.value === invoice.currency?.toLowerCase())?.label ?? '-',
      issueDate: invoice.issueDate ? formatDate(new Date(invoice.issueDate)) : null,
      dueDate: invoice.dueDate ? formatDate(new Date(invoice.dueDate)) : null,
      taxPoint: invoice.taxPoint ? formatDate(new Date(invoice.taxPoint)) : null,
      amountWithoutVat: getPriceWithCurrencyString(fromCentesimal(invoice.amountWithoutVat ?? 0), invoice.currency),
      amountVat: getPriceWithCurrencyString(fromCentesimal(invoice.amountVat ?? 0), invoice.currency),
      items: invoice.items.map((item) => ({
        ...item,
        priceSum: getPriceWithCurrencyString(fromCentesimal(item.priceSum), invoice.currency),
        pricePerUnit: getPriceWithCurrencyString(fromCentesimal(item.pricePerUnit), invoice.currency),
        priceWithoutVat: getPriceWithCurrencyString(fromCentesimal(item.priceWithoutVat ?? 0), invoice.currency),
        vatRate: item.vatRate ? `${item.vatRate}%` : undefined,
      })),
    };
  }, [data?.invoice]);

  const onArchived = (failed?: string[]) => {
    if (failed?.length) {
      notify.negative({ message: t('archive.failed') });
      return;
    }

    notify.positive({ message: t('archive.success') });
    safeNavigate('/invoice-management/invoices');
  };
  const onArchiveError = () => {
    notify.negative({ message: t('archive.failed') });
  };

  if (isLoading) {
    return <FullSizeLoader />;
  }

  return (
    <>
      <PageLayout>
        <PageHeader
          backRouteTo={-1}
          title={`${t('title')}`}
          subtitle={data?.invoice.number}
          Icon={ArrowBack}
          action={
            invoiceId ? (
              <Stack
                flex={1}
                minWidth={{ md: '100%' }}
                direction={'row'}
                gap={2}
                alignItems={'center'}
                justifyContent={{ md: 'flex-end' }}
                flexWrap={'wrap'}
              >
                <Button variant={'outlined'} component={Link} to={'copy'} startIcon={<FileCopy />}>
                  {t('copyInvoice')}
                </Button>
                {data?.invoice.status !== InvoiceStatus.Reminding ? (
                  <Button variant={'outlined'} component={Link} to={'edit'} startIcon={<Edit />}>
                    {t('editInvoice')}
                  </Button>
                ) : null}
                {data?.invoice.canBeArchived ? (
                  <InvoiceArchiveButton invoiceIds={[invoiceId]} onArchived={onArchived} onError={onArchiveError} />
                ) : null}
                <InvoiceManagementInvoiceDownloadButton data={pdfData} />
                <InvoiceManagementSendEmailButton data={pdfData} />
              </Stack>
            ) : null
          }
        />
        <PageContent
          sx={{
            flexDirection: 'column',
            gap: 4,
            justifyContent: 'flex-start',
          }}
        >
          {data?.invoice ? (
            <Box width={'100%'}>
              <InvoiceRemindingStatusBox invoice={data.invoice} />
            </Box>
          ) : null}
          <Box flex={1} display={'flex'} flexWrap={'wrap'} gap={2}>
            <Box
              flex={2}
              minWidth={{
                xs: 280,
                md: 724,
              }}
            >
              <Typography variant={'h3'} mb={2}>
                {t('invoiceInfo')}
              </Typography>

              <Paper variant={'outlined'} sx={{ flex: 1, p: 4, position: 'relative', maxWidth: '250mm' }}>
                {data?.invoice ? (
                  <Invoice
                    currency={data.invoice.currency}
                    invoice={{
                      ...(replaceNullOrUndefined({
                        ...data.invoice,
                        amount: fromCentesimal(data.invoice.amount ?? 0),
                        amountDue: fromCentesimal(data.invoice.amountDue ?? 0),
                        currency: data.invoice.currency?.toLowerCase(),
                      }) as InvoiceManagementInvoiceFormType),
                      items: data.invoice.items.map(
                        (item) =>
                          replaceNullOrUndefined({
                            ...item,
                            priceSum: fromCentesimal(item.priceSum),
                            pricePerUnit: fromCentesimal(item.pricePerUnit),
                            vatRate: item.vatRate ? item.vatRate : undefined,
                            priceWithoutVat: item.priceWithoutVat ? fromCentesimal(item.priceWithoutVat) : undefined,
                          }) as InvoiceItem & {
                            vatRate?: string | number | undefined;
                            priceWithoutVat?: string | number | undefined;
                          },
                      ),
                    }}
                  />
                ) : null}

                {isError ? (
                  <ExpandedBox>
                    <Typography color={'text.secondary'}>{t('error.query')}</Typography>
                  </ExpandedBox>
                ) : null}
              </Paper>
            </Box>
          </Box>
        </PageContent>
      </PageLayout>
      {/*OUTLET for modal children */}
      <Outlet />
    </>
  );
};

type Props = {
  invoice: InvoiceFragmentFragment;
  transNS?: string;
};

export const InvoiceRemindingStatusBox = (props: Props) => {
  const { t } = useTranslation();
  const properties = useMemo(() => getInvoiceStateProperties(props.invoice.status, props.invoice.id), [props.invoice]);

  const hasReminding = useMemo(() => !!props?.invoice.plan, [props?.invoice.plan]);

  return (
    <Stack direction={'row'} px={1} gap={2} flex={1} flexWrap={'wrap'} justifyContent={'center'}>
      {!hasReminding ? (
        <Paper
          sx={{
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            flex: 1,
            gap: 2,
            p: 2,
            width: { lg: '30%', md: '100%' },
            maxWidth: { lg: '30%', md: '100%' },
          }}
        >
          <Typography variant={'h5'}>Upomínání nenastaveno</Typography>
          <Box flex={1}>
            <Typography>
              Nastavte si snadno a rychle upomínání této faktury ve dvou krocích. Stačí doplnit kontaktní údaje a vybrat
              si balíček upomínaných služeb.
            </Typography>
          </Box>
          <LoadingButton
            variant={'contained'}
            endIcon={<ArrowForward />}
            component={Link}
            to={`/invoice-management/invoices/${props.invoice.id}/confirm-data`}
          >
            Nastavit upomínání
          </LoadingButton>
        </Paper>
      ) : (
        <Paper
          sx={{
            display: 'flex',
            height: '100%',
            flexDirection: 'column',
            flex: 1,
            gap: 2,
            p: 2,
            width: { lg: '30%', md: '100%' },
            maxWidth: { lg: '30%', md: '100%' },
          }}
        >
          <Typography variant={'h5'}>Upomínání nastaveno</Typography>
          <Box flex={1}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} gap={2}>
              <Skeleton
                animation={props.invoice.status === InvoiceStatus.Reminding ? 'pulse' : false}
                sx={{
                  bgcolor: properties.color,
                  width: 18,
                  height: 18,
                  borderRadius: '100%',
                  transform: 'none',
                }}
              />
              <Typography>{t(properties.transKey)}</Typography>
            </Stack>
          </Box>
          <LoadingButton endIcon={<ArrowForward />} component={Link} to={`/invoice/${props.invoice.id}`}>
            Zobrazit detail upomínání
          </LoadingButton>
        </Paper>
      )}
      {!props.invoice.settled ? (
        <SetInvoicePaidBox
          transNS={'page-invoice-management'}
          sx={{ width: { lg: '30%', md: '100%' }, maxWidth: { lg: '30%', md: '100%' } }}
        />
      ) : (
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 2,
            p: 2,
            width: { lg: '30%', md: '100%' },
            maxWidth: { lg: '30%', md: '100%' },
          }}
        >
          <Typography variant={'h5'}>Stav Faktury</Typography>
          <Box flex={1}>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Skeleton
                animation={false}
                sx={{
                  bgcolor: 'success.main',
                  width: 18,
                  height: 18,
                  borderRadius: '100%',
                  transform: 'none',
                }}
              />
              <Typography>Faktura byla úspěšně zaplacena</Typography>
            </Stack>
          </Box>
        </Paper>
      )}
    </Stack>
  );
};

const Invoice = (props: { invoice: InvoiceManagementInvoiceFormType; currency?: string | null }) => {
  const { control, setValue, getValues, reset } = useForm<InvoiceManagementInvoiceFormType>({
    defaultValues: props.invoice,
  });

  useEffect(() => {
    reset(props.invoice);
  }, [props.invoice, reset]);

  return (
    <InvoiceManagementInvoiceForm
      readonly={true}
      control={control}
      setValue={setValue}
      selectedCurrency={props.currency}
      getValues={getValues}
    />
  );
};
